import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { getContactById, getContactMetadata, updateContact } from '../services/contactService';
import { Spinner, Breadcrumb, BreadcrumbItem, Select } from '../../../shared/components';
import { Contact, ContactChannel, ContactChannelType, ContactMetadata } from '../types/Contact';
import { contactChannelTypeOptions, createEmptyContactChannel, ensurePrimaryContactChannels, getChannelLink, sortContactChannels } from '../contactChannelUtils';
import { getLanguageDisplayName, languageOptions, normalizeLanguageValue } from '../contactLanguageUtils';
import { resolveContactName } from '../contactNameUtils';
import { useAssetsModuleVisible } from '../../assets/assetsModule';
import { getAssets } from '../../assets/services/assetService';
import { assetTypePath } from '../../assets/typeCatalog';
import { assetTypeLabelKey, normalizeAssetType } from '../../assets/types/Asset';
import type { ContactAssetSummary } from '../types/Contact';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { listConsultations } from '../../consultations/services/consultationService';
import type { Consultation } from '../../consultations/types/Consultation';
import { RelatedConsultationsPanel } from '../../consultations/components/RelatedConsultationsPanel';
import { getConversations } from '../../conversations/services/conversationService';
import type { Conversation } from '../../conversations/types/Conversation';
import { ContactConversationsPanel } from './ContactConversationsPanel';
import { getCallingCapabilities, startOutboundCall } from '../../calling/services/callingService';
import { useToast } from '../../notifications';
import styles from './ContactFormLayout.module.css';

export const ContactDetails: React.FC = () => {
  const { t } = useTranslation(['contacts', 'consultations', 'conversations', 'common', 'assets', 'nav']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const isAssetsModuleVisible = useAssetsModuleVisible();
  const [contact, setContact] = React.useState<Contact | null>(null);
  const [linkedAssets, setLinkedAssets] = React.useState<ContactAssetSummary[]>([]);
  const [consultations, setConsultations] = React.useState<Consultation[]>([]);
  const [conversations, setConversations] = React.useState<Conversation[]>([]);
  const [metadata, setMetadata] = React.useState<ContactMetadata | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [consultationsLoading, setConsultationsLoading] = React.useState(false);
  const [conversationsLoading, setConversationsLoading] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<'details' | 'consultations' | 'conversations'>('details');
  const [form, setForm] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
    title: '',
    status: '',
    language: '',
    reminderPreference: '',
    marketingEmails: false,
    channels: [createEmptyContactChannel('phone')] as ContactChannel[]
  });
  const [errors, setErrors] = React.useState<{ [k: string]: string }>({});
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  const [callingEnabled, setCallingEnabled] = React.useState(false);
  const [callingBusyChannelId, setCallingBusyChannelId] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchContact = async () => {
      setLoading(true);
      try {
        const [metadataValue, contactData] = await Promise.all([
          getContactMetadata(),
          id?.trim() ? getContactById(id) : Promise.resolve(null),
        ]);
        setMetadata(metadataValue);
        if (id?.trim()) {
          setContact(contactData);
          if (contactData && isAssetsModuleVisible) {
            const assets = await getAssets({ contactPersonId: id });
            setLinkedAssets(assets.map(asset => ({
              id: asset.id,
              name: asset.name,
              assetType: asset.assetType,
              identifier: asset.identifier,
            })));
          }
          if (contactData) {
            setForm({
              firstName: contactData.firstName || '',
              lastName: contactData.lastName || '',
              email: contactData.email,
              title: contactData.title || '',
              status: contactData.status || metadataValue.statuses[0]?.value || '',
              language: normalizeLanguageValue(contactData.language),
              reminderPreference: contactData.reminderPreference || metadataValue.reminderPreferences[0]?.value || '',
              marketingEmails: contactData.marketingEmails,
              channels: contactData.channels.length > 0 ? sortContactChannels(contactData.channels) : [createEmptyContactChannel('phone')],
            });
          }
        }
      } catch {
        setGeneralError(t('failedToLoadContact', { ns: 'contacts' }));
      } finally {
        setLoading(false);
      }
    };
    fetchContact();
  }, [id, isAssetsModuleVisible, t]);

  React.useEffect(() => {
    if (!hasPermission(PermissionScopes.conversationsRead)) {
      setCallingEnabled(false);
      return;
    }

    let cancelled = false;
    const loadCapabilities = async () => {
      try {
        const capabilities = await getCallingCapabilities();
        if (!cancelled) {
          setCallingEnabled(capabilities.connectorConfigured && capabilities.outboundEnabled);
        }
      } catch {
        if (!cancelled) {
          setCallingEnabled(false);
        }
      }
    };

    void loadCapabilities();
    return () => {
      cancelled = true;
    };
  }, [hasPermission]);

  React.useEffect(() => {
    if (activeTab !== 'consultations' || !id?.trim()) {
      return;
    }

    let cancelled = false;
    const loadConsultations = async () => {
      setConsultationsLoading(true);
      try {
        const data = await listConsultations({ contactId: id, page: 1, pageSize: 100 });
        if (!cancelled) {
          setConsultations(data);
        }
      } catch {
        if (!cancelled) {
          setConsultations([]);
        }
      } finally {
        if (!cancelled) {
          setConsultationsLoading(false);
        }
      }
    };

    void loadConsultations();
    return () => {
      cancelled = true;
    };
  }, [activeTab, id]);

  React.useEffect(() => {
    if (activeTab !== 'conversations' || !id?.trim()) {
      return;
    }

    let cancelled = false;
    const loadConversations = async () => {
      setConversationsLoading(true);
      try {
        const data = await getConversations({ contactId: id, page: 1, pageSize: 100 });
        if (!cancelled) {
          setConversations(data);
        }
      } catch {
        if (!cancelled) {
          setConversations([]);
        }
      } finally {
        if (!cancelled) {
          setConversationsLoading(false);
        }
      }
    };

    void loadConversations();
    return () => {
      cancelled = true;
    };
  }, [activeTab, id]);

  const validate = () => {
    const e: { [k: string]: string } = {};
    if (!form.firstName.trim()) e.firstName = t('firstNameRequired', { ns: 'contacts' });
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t('invalidEmailAddress', { ns: 'contacts' });
    if (!form.email.trim() && !form.channels.some(channel => channel.value.trim().length > 0)) e.channels = t('channelsRequired', { ns: 'contacts' });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const setChannel = (index: number, updates: Partial<ContactChannel>) => {
    setForm(prev => {
      const currentChannel = prev.channels[index];
      if (!currentChannel) {
        return prev;
      }

      const nextChannels = prev.channels.map((channel, channelIndex) => channelIndex === index ? { ...channel, ...updates } : channel);
      if (updates.isPrimary) {
        const type = nextChannels[index]?.type;
        return {
          ...prev,
          channels: nextChannels.map((channel, channelIndex) => channel.type === type ? { ...channel, isPrimary: channelIndex === index } : channel),
        };
      }

      return { ...prev, channels: nextChannels };
    });
  };

  const addChannel = () => {
    setForm(prev => ({ ...prev, channels: [...prev.channels, createEmptyContactChannel('phone')] }));
  };

  const removeChannel = (index: number) => {
    setForm(prev => {
      const remaining = prev.channels.filter((_, channelIndex) => channelIndex !== index);
      return { ...prev, channels: remaining.length > 0 ? ensurePrimaryContactChannels(remaining) : [createEmptyContactChannel('phone')] };
    });
  };

  const onEdit = () => {
    setGeneralError(null);
    setEditMode(true);
  };

  const onCancel = () => {
    if (contact) {
      setForm({
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        email: contact.email,
        title: contact.title || '',
        status: contact.status || metadata?.statuses[0]?.value || '',
        language: normalizeLanguageValue(contact.language),
        reminderPreference: contact.reminderPreference || metadata?.reminderPreferences[0]?.value || '',
        marketingEmails: contact.marketingEmails,
        channels: contact.channels.length > 0 ? sortContactChannels(contact.channels) : [createEmptyContactChannel('phone')],
      });
    }
    setErrors({});
    setGeneralError(null);
    setEditMode(false);
  };

  const onSave = async () => {
    if (!contact) return;
    if (!validate()) return;
    setSaving(true);
    setGeneralError(null);
    try {
      const normalizedChannels = ensurePrimaryContactChannels(form.channels)
        .filter(channel => channel.value.trim().length > 0)
        .map(channel => ({
          id: channel.id,
          type: channel.type,
          label: channel.label.trim() || undefined,
          value: channel.value.trim(),
          isPrimary: channel.isPrimary,
        }));

      const phoneChannel = normalizedChannels.find(channel => channel.type === 'phone');
      const whatsAppChannel = normalizedChannels.find(channel => channel.type === 'whatsApp');

      const updated = await updateContact(contact.id, {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        title: form.title,
        phoneNumber: phoneChannel?.value,
        whatsApp: whatsAppChannel?.value,
        status: form.status,
        language: form.language,
        reminderPreference: form.reminderPreference,
        marketingEmails: form.marketingEmails,
        channels: normalizedChannels,
      });
      if (!updated) {
        setGeneralError(t('failedToSaveContactNotFound', { ns: 'contacts' }));
      } else {
        setContact(updated);
        setEditMode(false);
      }
    } catch (err) {
      setGeneralError(t('failedToSaveContact', { ns: 'contacts' }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
        <Spinner text={t('loadingContact', { ns: 'contacts' })} />
      </div>
    );
  }

  if (!contact) {
    return (
      <div className={styles.page}>
        <h2>{t('contactNotFound', { ns: 'contacts' })}</h2>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>{t('backToContacts', { ns: 'contacts' })}</button>
      </div>
    );
  }

  const resolvedContactName = resolveContactName(contact);
  const displayName = resolvedContactName.displayName || contact.name;

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: t('title', { ns: 'contacts' }), path: '/contacts' },
    { label: displayName || t('detailsTitle', { ns: 'contacts' }), path: `/contacts/${id}` }
  ];
  const detailChannels = sortContactChannels(contact.channels);
  const canCreateAsset = isAssetsModuleVisible && hasPermission(PermissionScopes.assetsCreate);
  const canViewConversations = hasPermission(PermissionScopes.conversationsRead);

  const startCall = async (channel: ContactChannel) => {
    if (!contact || channel.type !== 'phone' || !channel.value.trim()) {
      return;
    }

    setCallingBusyChannelId(channel.id ?? channel.value);
    setGeneralError(null);
    try {
      const session = await startOutboundCall({
        contactId: contact.id,
        contactChannelId: channel.id,
        phoneNumber: channel.value.trim(),
      });
      if (session.status === 'failed') {
        showToast(session.failureReason || t('failedToStartCall', { ns: 'contacts' }), 'error');
      } else {
        showToast(t('callStarted', { ns: 'contacts' }), 'success');
      }
      if (session.conversationId) {
        navigate(`/conversations/${session.conversationId}`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToStartCall', { ns: 'contacts' });
      setGeneralError(message);
      showToast(message, 'error');
    } finally {
      setCallingBusyChannelId(null);
    }
  };

  return (
    <div className={styles.page}>
      <Breadcrumb items={breadcrumbItems} />
      <div className={styles.header}>
        <h2 className={styles.title}>{t('detailsTitle', { ns: 'contacts' })}</h2>
      </div>
      {generalError && <div className={`error ${styles.errorBanner}`}>{generalError}</div>}

      {!editMode ? (
        <>
          <div className={styles.tabs}>
            <button
              type="button"
              className={activeTab === 'details' ? styles.tabButtonActive : styles.tabButton}
              onClick={() => setActiveTab('details')}
            >
              {t('detailsTitle', { ns: 'contacts' })}
            </button>
            <button
              type="button"
              className={activeTab === 'consultations' ? styles.tabButtonActive : styles.tabButton}
              onClick={() => setActiveTab('consultations')}
            >
              {t('consultations', { ns: 'consultations', defaultValue: 'Consultations' })}
            </button>
            {canViewConversations ? (
              <button
                type="button"
                className={activeTab === 'conversations' ? styles.tabButtonActive : styles.tabButton}
                onClick={() => setActiveTab('conversations')}
              >
                {t('conversations', { ns: 'nav', defaultValue: 'Conversations' })}
              </button>
            ) : null}
          </div>

          {activeTab === 'details' ? (
            <>
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('firstName', { ns: 'contacts' })}</p>
              <p className={`${styles.detailValue} ${resolvedContactName.firstName ? '' : styles.mutedValue}`}>{resolvedContactName.firstName || '-'}</p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('lastName', { ns: 'contacts' })}</p>
              <p className={`${styles.detailValue} ${resolvedContactName.lastName ? '' : styles.mutedValue}`}>{resolvedContactName.lastName || '-'}</p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('titleField', { ns: 'contacts' })}</p>
              <p className={`${styles.detailValue} ${contact.title ? '' : styles.mutedValue}`}>{contact.title || '-'}</p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('email', { ns: 'contacts' })}</p>
              <p className={`${styles.detailValue} ${contact.email ? '' : styles.mutedValue}`}>
                {contact.email ? <a className={styles.detailLink} href={`mailto:${contact.email}`}>{contact.email}</a> : '-'}
              </p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('status', { ns: 'contacts' })}</p>
              <p className={`${styles.detailValue} ${contact.status ? '' : styles.mutedValue}`}>{contact.status || '-'}</p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('language', { ns: 'contacts' })}</p>
              <p className={`${styles.detailValue} ${contact.language ? '' : styles.mutedValue}`}>
                {getLanguageDisplayName(contact.language, metadata?.languages) || '-'}
              </p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('reminderPreference', { ns: 'contacts' })}</p>
              <p className={`${styles.detailValue} ${contact.reminderPreference ? '' : styles.mutedValue}`}>{contact.reminderPreference || '-'}</p>
            </div>
            <div className={styles.detailItem}>
              <p className={styles.detailLabel}>{t('marketingEmails', { ns: 'contacts' })}</p>
              <p className={styles.detailValue}>{contact.marketingEmails ? t('yes', { ns: 'contacts' }) : t('no', { ns: 'contacts' })}</p>
            </div>
            <div className={`${styles.detailItem} ${styles.detailItemWide}`}>
              <p className={styles.detailLabel}>{t('channels', { ns: 'contacts' })}</p>
              {detailChannels.length > 0 ? (
                <div className={styles.detailChannelList}>
                  {detailChannels.map(channel => {
                    const href = getChannelLink(channel);
                    return (
                      <div key={`${channel.id ?? `${channel.type}-${channel.value}`}`} className={styles.detailChannelRow}>
                        <span className={styles.channelTypeBadge}>{t(`channelTypeOptions.${channel.type}`, { ns: 'contacts' })}</span>
                        <span className={styles.detailChannelValue}>{channel.value}</span>
                        {href && !(callingEnabled && channel.type === 'phone') ? (
                          <a className={styles.detailLink} href={href} target="_blank" rel="noreferrer">
                            {t('openChannelProfile', { ns: 'contacts' })}
                          </a>
                        ) : null}
                        {callingEnabled && channel.type === 'phone' && channel.value.trim() ? (
                          <button
                            type="button"
                            className={styles.detailLink}
                            style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
                            onClick={() => { void startCall(channel); }}
                            disabled={callingBusyChannelId === (channel.id ?? channel.value)}
                          >
                            {callingBusyChannelId === (channel.id ?? channel.value)
                              ? t('startingCall', { ns: 'contacts' })
                              : t('callContact', { ns: 'contacts' })}
                          </button>
                        ) : null}
                        <span className={`${styles.detailChannelMeta} ${channel.label ? '' : styles.mutedValue}`}>{channel.label || t('noLabel', { ns: 'contacts' })}</span>
                        {channel.isPrimary ? <span className={styles.primaryBadge}>{t('primary', { ns: 'contacts' })}</span> : null}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className={`${styles.detailValue} ${styles.mutedValue}`}>-</p>
              )}
            </div>
            {isAssetsModuleVisible ? (
              <div className={`${styles.detailItem} ${styles.detailItemWide}`}>
                <p className={styles.detailLabel}>{t('assets', { ns: 'contacts', defaultValue: 'Assets' })}</p>
                {linkedAssets.length > 0 ? (
                  <div className={styles.detailChannelList}>
                    {linkedAssets.map(asset => (
                      <div key={asset.id} className={styles.detailChannelRow}>
                        <a className={styles.detailLink} href={assetTypePath(normalizeAssetType(asset.assetType), `/${asset.id}`)}>{asset.name}</a>
                        <span className={styles.detailChannelValue}>{t(assetTypeLabelKey(normalizeAssetType(asset.assetType)), { ns: 'assets', defaultValue: asset.assetType })}{asset.identifier ? ` · ${asset.identifier}` : ''}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`${styles.detailValue} ${styles.mutedValue}`}>-</p>
                )}
              </div>
            ) : null}
          </div>
          <div className={styles.actions}>
            {canCreateAsset ? <button className="btn btn-secondary" onClick={() => navigate(`/assets/add?contactId=${contact.id}`)}>{t('addAsset', { ns: 'contacts', defaultValue: 'Add Asset' })}</button> : null}
            <button className="btn btn-primary" onClick={onEdit}>{t('edit', { ns: 'common' })}</button>
            <button className="btn btn-secondary" onClick={() => navigate('/contacts')}>{t('backToContacts', { ns: 'contacts' })}</button>
          </div>
            </>
          ) : activeTab === 'consultations' ? (
            consultationsLoading ? (
              <Spinner text={t('loadingConsultations', { ns: 'contacts', defaultValue: 'Loading consultations…' })} />
            ) : (
              <RelatedConsultationsPanel
                consultations={consultations}
                emptyLabel={t('noContactConsultations', { ns: 'contacts', defaultValue: 'No consultations are linked to this contact.' })}
                returnTo={`/contacts/${contact.id}`}
              />
            )
          ) : conversationsLoading ? (
            <Spinner text={t('loadingConversations', { ns: 'contacts', defaultValue: 'Loading conversations…' })} />
          ) : (
            <ContactConversationsPanel
              conversations={conversations}
              emptyLabel={t('noContactConversations', { ns: 'contacts', defaultValue: 'No conversations are linked to this contact.' })}
              returnTo={`/contacts/${contact.id}`}
            />
          )}
        </>
      ) : (
        <>
          <div className={styles.formGrid}>
          <div className={styles.field}>
            <label className={styles.label}>
              {t('firstName', { ns: 'contacts' })}
              <input className={styles.input} value={form.firstName} onChange={e => setForm(prev => ({ ...prev, firstName: e.target.value }))} />
            </label>
            {errors.firstName && <div className={styles.fieldError}>{errors.firstName}</div>}
          </div>
          <div className={styles.field}>
            <label className={styles.label}>
              {t('lastName', { ns: 'contacts' })}
              <input className={styles.input} value={form.lastName} onChange={e => setForm(prev => ({ ...prev, lastName: e.target.value }))} />
            </label>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>
              {t('email', { ns: 'contacts' })}
              <input
                className={styles.input}
                value={form.email}
                onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
              />
            </label>
            {errors.email && <div className={styles.fieldError}>{errors.email}</div>}
          </div>
          <div className={styles.field}>
            <label className={styles.label}>
              {t('titleField', { ns: 'contacts' })}
              <input className={styles.input} value={form.title} onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))} />
            </label>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>
              {t('status', { ns: 'contacts' })}
              <Select
                value={form.status}
                onChange={(value) => setForm(prev => ({ ...prev, status: value }))}
                options={(metadata?.statuses || []).map(option => ({
                  value: option.value,
                  label: option.label,
                }))}
              />
            </label>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>
              {t('language', { ns: 'contacts' })}
              <Select
                value={form.language}
                onChange={(value) => setForm(prev => ({ ...prev, language: value }))}
                options={languageOptions.map(option => ({
                  value: option.code,
                  label: option.label,
                }))}
              />
            </label>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>
              {t('reminderPreference', { ns: 'contacts' })}
              <Select
                value={form.reminderPreference}
                onChange={(value) => setForm(prev => ({ ...prev, reminderPreference: value }))}
                options={(metadata?.reminderPreferences || []).map(option => ({
                  value: option.value,
                  label: option.label,
                }))}
              />
            </label>
          </div>
          <div className={`${styles.field} ${styles.fieldFull}`}>
            <label className={styles.primaryToggle}>
              <input type="checkbox" checked={form.marketingEmails} onChange={e => setForm(prev => ({ ...prev, marketingEmails: e.target.checked }))} />
              <span>{t('marketingEmails', { ns: 'contacts' })}</span>
            </label>
          </div>
          <div className={`${styles.field} ${styles.fieldFull}`}>
            <div className={styles.channelsHeader}>
              <label className={styles.label}>{t('channels', { ns: 'contacts' })}</label>
              <button type="button" className="btn btn-secondary" onClick={addChannel}>{t('addChannel', { ns: 'contacts' })}</button>
            </div>
            <div className={styles.fieldHint}>{t('telegramChannelManagedHint', { ns: 'contacts' })}</div>
            <div className={styles.channelsList}>
              {form.channels.map((channel, index) => (
                <div key={`${channel.id ?? channel.type}-${index}`} className={styles.channelRow}>
                  <div className={styles.channelGrid}>
                    <label className={styles.label}>
                      {t('channelType', { ns: 'contacts' })}
                      <Select
                        value={channel.type}
                        onChange={(value) => setChannel(index, { type: value as ContactChannelType, isPrimary: channel.isPrimary })}
                        options={contactChannelTypeOptions.map(type => ({
                          value: type,
                          label: t(`channelTypeOptions.${type}`, { ns: 'contacts' }),
                        }))}
                      />
                    </label>
                    <label className={styles.label}>
                      {t('channelLabel', { ns: 'contacts' })}
                      <input className={styles.input} value={channel.label} onChange={e => setChannel(index, { label: e.target.value })} placeholder={t('channelLabelPlaceholder', { ns: 'contacts' })} />
                    </label>
                    <label className={styles.label}>
                      {t('channelValue', { ns: 'contacts' })}
                      <input className={styles.input} value={channel.value} onChange={e => setChannel(index, { value: e.target.value })} placeholder={t('channelValuePlaceholder', { ns: 'contacts' })} />
                    </label>
                    <label className={styles.primaryToggle}>
                      <input type="radio" name={`primary-${channel.type}`} checked={channel.isPrimary} onChange={() => setChannel(index, { isPrimary: true })} />
                      <span>{t('primary', { ns: 'contacts' })}</span>
                    </label>
                  </div>
                  <div className={styles.channelActions}>
                    <button type="button" className="btn btn-secondary" onClick={() => removeChannel(index)} disabled={form.channels.length === 1}>{t('removeChannel', { ns: 'contacts' })}</button>
                  </div>
                  {channel.type === 'telegram' ? <div className={styles.fieldHint}>{t('telegramChannelReadOnlyHint', { ns: 'contacts' })}</div> : null}
                </div>
              ))}
            </div>
            <div className={styles.fieldHint}>{t('channelsHint', { ns: 'contacts' })}</div>
            {errors.channels && <div className={styles.fieldError}>{errors.channels}</div>}
          </div>
          </div>
          <div className={styles.actions}>
            <button className="btn btn-primary" onClick={onSave} disabled={saving}>{saving ? t('saving', { ns: 'common' }) : t('save', { ns: 'contacts' })}</button>
            <button className="btn btn-secondary" onClick={onCancel} disabled={saving}>{t('cancel', { ns: 'common' })}</button>
          </div>
        </>
      )}
    </div>
  );
};